/**
 * Lighten a hex color by a percentage
 */
export function lightenColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16)
  const r = Math.min(255, ((num >> 16) & 0xff) + Math.round(255 * (percent / 100)))
  const g = Math.min(255, ((num >> 8) & 0xff) + Math.round(255 * (percent / 100)))
  const b = Math.min(255, (num & 0xff) + Math.round(255 * (percent / 100)))
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`
}

/**
 * Darken a hex color by a percentage
 */
export function darkenColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16)
  const r = Math.max(0, ((num >> 16) & 0xff) - Math.round(255 * (percent / 100)))
  const g = Math.max(0, ((num >> 8) & 0xff) - Math.round(255 * (percent / 100)))
  const b = Math.max(0, (num & 0xff) - Math.round(255 * (percent / 100)))
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`
}

/**
 * Returns '#000000' or '#ffffff' — whichever has better contrast against the
 * given hex color, using the WCAG relative luminance formula.
 */
export function getContrastText(hex: string): string {
  const num = parseInt(hex.replace('#', ''), 16)
  const toLinear = (c: number) => {
    const s = c / 255
    return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  }
  const r = toLinear((num >> 16) & 0xff)
  const g = toLinear((num >> 8) & 0xff)
  const b = toLinear(num & 0xff)
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b
  return luminance > 0.179 ? '#000000' : '#ffffff'
}

/**
 * Compute the contrast ratio between two hex colours per WCAG 2.x.
 *
 * Returns a value in [1, 21]. AA requires >= 4.5 for normal text and >= 3
 * for large text / UI components. Used by the branding settings tab to warn
 * admins about combinations that will be hard to read.
 */
export function getContrastRatio(fg: string, bg: string): number {
  const luminance = (hex: string): number => {
    const num = parseInt(hex.replace('#', ''), 16)
    const toLinear = (c: number): number => {
      const s = c / 255
      return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
    }
    const r = toLinear((num >> 16) & 0xff)
    const g = toLinear((num >> 8) & 0xff)
    const b = toLinear(num & 0xff)
    return 0.2126 * r + 0.7152 * g + 0.0722 * b
  }
  const l1 = luminance(fg)
  const l2 = luminance(bg)
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05)
}

/**
 * Return true when the fg/bg pair meets WCAG AA (4.5:1 for normal text).
 */
export function passesWCAG_AA(fg: string, bg: string): boolean {
  return getContrastRatio(fg, bg) >= 4.5
}

export interface BrandColors {
  /** Button/accent background. Required. */
  primaryColor: string
  /** Navigation background. Null → fall back to the default in tokens.css. */
  navBackgroundColor?: string | null
  /** Navigation text. Null → fall back to the default in tokens.css. */
  navTextColor?: string | null
  /** Button text override. Null → auto-derive from primaryColor via luminance. */
  buttonTextColor?: string | null
}

/**
 * Apply brand color CSS variables to the document root.
 *
 * Accepts either a single hex (legacy signature) or the four-colour
 * ``BrandColors`` shape. When a nav colour is null the pre-existing token
 * default (matching the current live app) is retained.
 */
export function applyBrandColors(input: string | BrandColors) {
  const colors: BrandColors =
    typeof input === 'string' ? { primaryColor: input } : input

  const {
    primaryColor,
    navBackgroundColor,
    navTextColor,
    buttonTextColor,
  } = colors

  const root = document.documentElement.style

  // Primary (button background) family — unchanged behaviour.
  root.setProperty('--brand-primary', primaryColor)
  root.setProperty('--brand-primary-hover', lightenColor(primaryColor, 10))
  root.setProperty('--brand-primary-light', lightenColor(primaryColor, 20))
  root.setProperty('--brand-primary-lighter', lightenColor(primaryColor, 40))
  root.setProperty('--brand-primary-lightest', lightenColor(primaryColor, 90))
  root.setProperty('--brand-primary-active', darkenColor(primaryColor, 10))
  root.setProperty('--brand-primary-dark', darkenColor(primaryColor, 20))

  // Button text: explicit override wins, else auto-contrast (legacy default).
  const contrastText =
    buttonTextColor && buttonTextColor.length > 0
      ? buttonTextColor
      : getContrastText(primaryColor)
  root.setProperty('--brand-primary-text', contrastText)

  // Nav surface colours (DEV-48214). Always write an explicit value so a
  // stale null-caller can't race a fresh caller and blank out the override.
  // When the tenant has no override we set the same fallback the tokens.css
  // defaults use — visually identical for existing tenants.
  const resolvedNavBg = navBackgroundColor || '#ffffff'
  const resolvedNavText = navTextColor || '#374151'
  root.setProperty('--nav-bg', resolvedNavBg)
  root.setProperty('--nav-text', resolvedNavText)
  // Derive inactive-nav colour from the resolved nav text + nav bg. Uses
  // color-mix so the derived value naturally has usable contrast against the
  // nav background regardless of what the admin picked. Fixes Chris's
  // bottom-nav readability complaint on dark themes (§4 of the ticket doc).
  root.setProperty(
    '--nav-text-inactive',
    `color-mix(in oklab, ${resolvedNavText} 55%, ${resolvedNavBg})`,
  )

  // WorkOS widget accent color
  root.setProperty('--woswidgets-accent-color', primaryColor)

  // Radix accent overrides for WorkOS widgets (buttons, modals)
  root.setProperty('--accent-1', lightenColor(primaryColor, 95))
  root.setProperty('--accent-2', lightenColor(primaryColor, 90))
  root.setProperty('--accent-3', lightenColor(primaryColor, 80))
  root.setProperty('--accent-4', lightenColor(primaryColor, 70))
  root.setProperty('--accent-5', lightenColor(primaryColor, 60))
  root.setProperty('--accent-6', lightenColor(primaryColor, 45))
  root.setProperty('--accent-7', lightenColor(primaryColor, 30))
  root.setProperty('--accent-8', lightenColor(primaryColor, 15))
  root.setProperty('--accent-9', primaryColor)
  root.setProperty('--accent-10', darkenColor(primaryColor, 8))
  root.setProperty('--accent-11', darkenColor(primaryColor, 20))
  root.setProperty('--accent-12', darkenColor(primaryColor, 35))
  root.setProperty('--accent-a9', primaryColor)
  root.setProperty('--accent-a10', darkenColor(primaryColor, 8))
  root.setProperty('--accent-contrast', contrastText)
}
